import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Buildings, ShieldCheck, Clock } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div>
      {/* Hero */}
      <section className="relative border-b border-neutral-200 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="label-eyebrow mb-6">Workspaces · By the hour</div>
              <h1 className="font-display font-medium tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
                A quieter desk,<br/>
                <span className="text-neutral-500">two blocks away.</span>
              </h1>
              <p className="mt-8 max-w-xl text-neutral-600 text-lg leading-relaxed">
                DenShare connects people who need a focused hour with hosts who have a spare
                office — a real chair, a real monitor, a real door that closes.
              </p>
              <div className="mt-10 flex items-center gap-4">
                <Link to="/browse">
                  <Button data-testid="hero-browse-btn" className="h-12 px-6 rounded-md bg-neutral-900 hover:bg-neutral-800 text-white">
                    Browse dens <ArrowUpRight size={18} className="ml-1"/>
                  </Button>
                </Link>
                <Link to="/host">
                  <Button data-testid="hero-host-btn" variant="outline" className="h-12 px-6 rounded-md border-neutral-300 hover:bg-neutral-50">
                    List your workspace
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 grid grid-cols-2 gap-4">
              <div className="aspect-square overflow-hidden border border-neutral-200">
                <img src="https://images.unsplash.com/photo-1510519138101-570d1dca3d66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob21lJTIwb2ZmaWNlJTIwZGVzayUyMHNldHVwfGVufDB8fHx8MTc4NjUwMjcwOXww&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover" alt=""/>
              </div>
              <div className="aspect-square overflow-hidden border border-neutral-200 translate-y-8">
                <img src="https://images.unsplash.com/photo-1683836809851-9e3aad661ffd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxlcmdvbm9taWMlMjBob21lJTIwd29ya3NwYWNlJTIwbWluaW1hbHxlbnwwfHx8fDE3ODY1MDI3MDl8MA&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover" alt=""/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
        {[
          { icon: <Clock size={22} weight="regular"/>, title: "By the hour", body: "Book a real workspace for 60 minutes or a half-day. No monthly memberships." },
          { icon: <Buildings size={22} weight="regular"/>, title: "Real homes, real dens", body: "Each listing is a private room in someone's home — not a co-working chain." },
          { icon: <ShieldCheck size={22} weight="regular"/>, title: "Every host, verified", body: "ID-verified hosts, hourly calendars, and double-booking is impossible." },
        ].map((f) => (
          <div key={f.title} className="bg-white p-10">
            <div className="w-10 h-10 border border-neutral-300 flex items-center justify-center mb-6">{f.icon}</div>
            <h3 className="font-display font-medium text-xl mb-2">{f.title}</h3>
            <p className="text-neutral-600 text-sm leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="border border-neutral-900 bg-neutral-900 text-white p-12 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="label-eyebrow text-neutral-400 mb-4">For hosts</div>
            <h2 className="font-display font-medium text-3xl md:text-4xl tracking-tight max-w-xl">
              Turn your empty afternoon office into a booked desk.
            </h2>
          </div>
          <Link to="/signup">
            <Button data-testid="cta-signup-btn" className="h-12 px-6 rounded-md bg-white text-neutral-900 hover:bg-neutral-100">
              Start hosting
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
